// Catálogo da Classificação regional (PRD §4.9). Paridade literal com ANM (§4.7–4.8),
// porém: (1) cada norma tem catálogo/notas/faixas próprios; (2) há um fluxo de entrada
// estado → norma; (3) não há seção Gestão operacional.
//
// As notas e os limites de faixa são transcritos dos PDFs normativos versionados em
// docs/prd/admin/ (decreto-regional-mg.pdf — anexos I/II/III; IGAM.pdf — Anexo IV).

/** Estados com normas regionais cadastradas. v1: somente MG. */
export type RegionalState = "mg";

/**
 * UFs ofertadas no select de Estado (catálogo de referência — F22).
 * Fluxo: o usuário escolhe a UF e só então o radio mostra as normas daquela UF
 * (ver REGIONAL_NORMS_BY_STATE). v1: somente MG. Para um 2º estado, adicionar a UF
 * aqui, marcar `state` nas novas normas do template e cadastrar i18n/colunas.
 */
export const REGIONAL_STATES: readonly RegionalState[] = ["mg"] as const;

/**
 * Normas regionais (v1 MG). Os "3 decretos" são 3 anexos do Decreto nº 48.140/2021
 * (indústria, mineração, água) + a Portaria IGAM. Valores enum em inglês (PRD §6).
 */
export type RegionalNorm = "mgIndustry" | "mgMining" | "mgWater" | "igam";

export const REGIONAL_NORMS: readonly RegionalNorm[] = [
  "mgIndustry",
  "mgMining",
  "mgWater",
  "igam",
] as const;

/** Rótulo da 2ª seção: PDA nos anexos do decreto, DPA no IGAM (PRD §4.9). */
export type RegionalSecondSectionKey = "pda" | "dpa";

/** Limites de faixa de uma seção (Alto/Médio/Baixo). */
export type RegionalBandThresholds = {
  /** total >= highMin → Alto. */
  highMin: number;
  /** total > mediumMin (e < highMin) → Médio; caso contrário Baixo. */
  mediumMin: number;
};

/**
 * Faixas + regra especial EC por norma (PRD §4.9, verificado nos PDFs):
 * - mgIndustry/mgMining: CRI 65/37, EC nota 10, PDA 13/7.
 * - mgWater/igam:        CRI 60/35, EC nota 8,  PDA/DPA 16/10.
 */
export type RegionalNormBands = {
  cri: RegionalBandThresholds & {
    /** Nota em qualquer coluna EC que força CRI Alto (independe do total). */
    ecHighScore: number;
  };
  second: RegionalBandThresholds;
};

export type RegionalCatalogOptionTemplate = {
  score: number;
  textKey: string;
};

export type RegionalCatalogColumnTemplate = {
  /** Chave plana persistida (ex.: "cri-ct-a", "pda-a") — contrato do back. */
  key: string;
  letter: string;
  titleKey: string;
  options: RegionalCatalogOptionTemplate[];
};

export type RegionalCatalogGradeTemplate = {
  key: string;
  code: string;
  fullTitleKey: string;
  columns: RegionalCatalogColumnTemplate[];
};

export type RegionalCatalogSectionTemplate = {
  key: "cri" | RegionalSecondSectionKey;
  labelKey: string;
  subtitleKey: string;
  hasBignumber: boolean;
  grades: RegionalCatalogGradeTemplate[];
};

export type RegionalNormTemplate = {
  norm: RegionalNorm;
  /** UF à qual a norma pertence — fonte do filtro estado → normas no radio. */
  state: RegionalState;
  labelKey: string;
  secondSectionKey: RegionalSecondSectionKey;
  bands: RegionalNormBands;
  sections: RegionalCatalogSectionTemplate[];
};

// ---- Tipos já traduzidos (consumidos pela UI, espelham os do catálogo ANM). ----

export type CardOption = { score: number; text: string };

export type ColumnDef = {
  key: string;
  letter: string;
  title: string;
  options: CardOption[];
};

export type GradeDef = {
  key: string;
  code: string;
  fullTitle: string;
  columns: ColumnDef[];
};

export type SectionDef = {
  key: "cri" | RegionalSecondSectionKey;
  label: string;
  subtitle: string;
  hasBignumber: boolean;
  grades: GradeDef[];
};

export type RegionalNormDef = {
  norm: RegionalNorm;
  state: RegionalState;
  label: string;
  secondSectionKey: RegionalSecondSectionKey;
  bands: RegionalNormBands;
  sections: SectionDef[];
};

export type ClassificationLevel = "high" | "medium" | "low";
